import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { validateEmail, validatePasswordStrength, validatePhone } from 'shared';
import PasswordInput from '@/components/PasswordInput';
import ReCaptcha from '@/components/ReCaptcha';
import { useGuardianRegister } from '@/hooks/useGuardian';
import { ApiClientError } from '@/lib/api';
import { passwordRequirementsMessage } from '@/lib/errors';

type Field = 'nome' | 'apelido' | 'email' | 'telemovel' | 'password' | 'confirmPassword' | 'captcha';
type FieldErrors = Partial<Record<Field, string>>;

function registerError(err: unknown): string {
  if (err instanceof ApiClientError && err.status === 409) {
    return 'Já existe uma conta de Encarregado de Educação com este email.';
  }
  return 'Não foi possível criar a conta. Tente novamente.';
}

/**
 * Sign-up for an Encarregado de Educação. The account waits for the Centro to
 * approve it, and the Centro links it to the guardian's educandos: nothing
 * here chooses whose data the guardian will see.
 */
export default function GuardianRegisterPage() {
  const [values, setValues] = useState<Record<Exclude<Field, 'captcha'>, string>>({
    nome: '', apelido: '', email: '', telemovel: '', password: '', confirmPassword: '',
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [done, setDone] = useState(false);
  const register = useGuardianRegister();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const set = (field: Exclude<Field, 'captcha'>) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = (): FieldErrors => {
    const found: FieldErrors = {};
    if (!values.nome.trim()) found.nome = 'O nome é obrigatório.';
    if (!values.apelido.trim()) found.apelido = 'O apelido é obrigatório.';
    if (!validateEmail(values.email.trim()).valid) found.email = 'Indique um email válido (ex.: nome@exemplo.com).';
    if (!validatePhone(values.telemovel.trim()).valid) found.telemovel = 'Indique um número de telemóvel válido.';
    const passwordError = passwordRequirementsMessage(validatePasswordStrength(values.password));
    if (passwordError) found.password = passwordError;
    if (values.password !== values.confirmPassword) found.confirmPassword = 'As palavras-passe não coincidem.';
    if (import.meta.env.VITE_RECAPTCHA_SITE_KEY && !captchaToken) found.captcha = 'Conclua o captcha para continuar.';
    return found;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    register.mutate(
      {
        nome: values.nome.trim(),
        apelido: values.apelido.trim(),
        email: values.email.trim(),
        telemovel: values.telemovel.trim(),
        password: values.password,
        captchaToken: captchaToken || undefined,
      },
      {
        onSuccess: () => setDone(true),
        onError: () => {
          recaptchaRef.current?.reset();
          setCaptchaToken(null);
        },
      },
    );
  };

  if (done) {
    return (
      <Shell>
        <div role="status" className="mt-8 space-y-4 rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-black text-foreground">Pedido enviado</h2>
          <p className="text-sm text-muted-foreground">
            A equipa do Centro vai confirmar a sua conta e associá-la aos seus educandos. Depois
            disso, pode iniciar sessão com o seu email.
          </p>
          <button type="button" className="btn-primary w-full" onClick={() => navigate('/login?conta=ee', { replace: true })}>
            Voltar ao início de sessão
          </button>
        </div>
      </Shell>
    );
  }

  const errorList = Object.values(errors);
  const banner = errorList.length > 1 ? 'Corrija os campos assinalados abaixo.' : errorList[0];

  return (
    <Shell>
      <form className="mt-8 flex flex-col gap-4" onSubmit={handleSubmit} aria-busy={register.isPending} noValidate>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          {(banner || register.isError) && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {banner || registerError(register.error)}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <TextField id="nome" label="Nome" autoComplete="given-name" value={values.nome} onChange={set('nome')} error={errors.nome} />
          <TextField id="apelido" label="Apelido" autoComplete="family-name" value={values.apelido} onChange={set('apelido')} error={errors.apelido} />
        </div>
        <TextField id="email" label="Email" type="email" autoComplete="email" value={values.email} onChange={set('email')} error={errors.email} />
        <TextField id="telemovel" label="Telemóvel" type="tel" autoComplete="tel" value={values.telemovel} onChange={set('telemovel')} error={errors.telemovel} />
        <TextField id="password" label="Palavra-passe" type="password" autoComplete="new-password" value={values.password} onChange={set('password')} error={errors.password} />
        <TextField id="confirmPassword" label="Confirmar palavra-passe" type="password" revealLabel="confirmação da palavra-passe" autoComplete="new-password" value={values.confirmPassword} onChange={set('confirmPassword')} error={errors.confirmPassword} />

        <p className="form-hint">
          Use o mesmo email ou telemóvel que deu ao Centro quando inscreveu os seus educandos: ajuda
          a equipa a encontrá-los.
        </p>

        <ReCaptcha ref={recaptchaRef} onChange={setCaptchaToken} onExpired={() => setCaptchaToken(null)} />

        <p className="text-xs text-muted-foreground">
          Ao pedir conta, os seus dados são tratados como explicado em{' '}
          <Link to="/privacidade" className="font-semibold text-primary underline-offset-2 hover:underline">
            Privacidade
          </Link>
          .
        </p>

        <button type="submit" disabled={register.isPending} className="btn-primary mt-2 w-full">
          {register.isPending ? 'A enviar…' : 'Pedir conta'}
        </button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Helmet>
        <title>Conta de Encarregado de Educação | AICD</title>
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-background">
        <div className="relative z-10 mx-auto w-full max-w-md px-6 pt-8 pb-12">
          <Link to="/login?conta=ee" className="back-link">
            Voltar
          </Link>
          <header className="mt-2">
            <h1 className="text-[26px] font-extrabold tracking-tight text-foreground">
              Encarregado de Educação
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Peça uma conta para acompanhar os seus educandos nos Centros DIGGER.
            </p>
          </header>
          {children}
        </div>
      </div>
    </>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: string;
  autoComplete?: string;
  revealLabel?: string;
}

function TextField({ id, label, value, onChange, error, type = 'text', autoComplete, revealLabel }: TextFieldProps) {
  const inputProps = {
    id,
    autoComplete,
    required: true,
    value,
    onChange,
    'aria-invalid': !!error || undefined,
    'aria-describedby': error ? `${id}-error` : undefined,
  };
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      {type === 'password' ? (
        <PasswordInput {...inputProps} revealLabel={revealLabel} />
      ) : (
        <input {...inputProps} type={type} className="input" />
      )}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
